import React from 'react';
import _ from 'lodash';
import validateUrl from '../../../Utils/UrlValidator';

export const BotQuestionsMixin = {
  initializeBotQuestions(questions = []) {
    const orderMap = _.reduce(
      questions,
      (acc, question) => {
        acc[question.Order] = false;
        return acc;
      },
      {}
    );
    return {
      showFollowupQuestions: orderMap,
      categoryErrors: {},
      questionErrors: {},
    };
  },

  addQuestion(questions, order, isPaidJobServiceEnabled = false) {
    const clonedQuestions = _.cloneDeep(questions);
    clonedQuestions.push({
      Question: '',
      ExpectedAnswer: 'Yes',
      CategoryName: 'Filtering',
      Order: order,
    });

    if (isPaidJobServiceEnabled && this.lastQuestionRef?.current) {
      setTimeout(() => this.lastQuestionRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' }), 0);
    }

    return clonedQuestions;
  },

  removeQuestion(questions, order) {
    let updatedQuestions;
    const removedOrders = [];
    const orderUpdateMapper = {};
    if (!order.includes('.')) {
      updatedQuestions = questions
        .filter(question => {
          if (question.Order.split('.')[0] === order) {
            removedOrders.push(question.Order);
            return false;
          }
          return true;
        })
        .map(question => {
          const oldOrder = question.Order;
          const parts = oldOrder.split('.');
          if (Number(parts[0]) > Number(order)) {
            parts[0] = String(Number(parts[0]) - 1);
            const newOrder = parts.join('.');
            orderUpdateMapper[oldOrder] = newOrder;
            // eslint-disable-next-line no-param-reassign
            question.Order = newOrder;
          }
          return question;
        });
    } else {
      updatedQuestions = questions.filter(question => {
        if (question.Order === order || question.Order.startsWith(`${order}.`)) {
          removedOrders.push(question.Order);
          return false;
        }
        return true;
      });
    }
    return { updatedQuestions, removedOrders, orderUpdateMapper };
  },

  removeChildQuestions(questions, order) {
    const removedOrders = questions
      .filter(question => question.Order.startsWith(`${order}.`))
      .map(question => question.Order);

    const updatedQuestions = questions.filter(question => !question.Order.startsWith(`${order}.`));

    return { updatedQuestions, removedOrders };
  },

  onQuestionFieldChange(questions, field, order, value) {
    const updatedQuestions = _.cloneDeep(questions);
    const targetQuestion = _.find(updatedQuestions, { Order: order });
    if (targetQuestion) {
      _.set(targetQuestion, field, value);
    }
    return updatedQuestions;
  },

  onCategoryChange(questions, order, value) {
    const updatedQuestions = _.cloneDeep(questions);
    const targetQuestion = _.find(updatedQuestions, { Order: order });
    if (!targetQuestion) return updatedQuestions;

    targetQuestion.ExpectedAnswer = value;
    switch (value.toLowerCase()) {
      case 'yes':
        targetQuestion.CategoryName = 'Filtering';
        break;
      case 'phonenumber':
        targetQuestion.CategoryName = 'Call';
        targetQuestion.Question = 'What is the best number to call you?';
        break;
      case 'appointment':
        targetQuestion.CategoryName = 'Appointment';
        targetQuestion.Question = 'What is the best time to call you?';
        break;
      case 'registration':
        targetQuestion.CategoryName = 'Registration';
        targetQuestion.Question = 'Which hiring fair day would you like to attend?';
        targetQuestion.ExpectedAnswer = 'REGISTRATION';
        break;
      case 'interviewlocation':
        targetQuestion.CategoryName = 'INTERVIEWLOCATION';
        targetQuestion.Question = 'Would you be able to attend an in-person interview at our location in ';
        targetQuestion.ExpectedAnswer = 'INTERVIEWLOCATION';
        break;
      case 'closetext':
        targetQuestion.CategoryName = 'CLOSETEXT';
        break;
      case 'freetext':
        targetQuestion.CategoryName = '';
        break;
      default:
        break;
    }
    return updatedQuestions;
  },

  moveQuestions(questions, dragIndex, hoverIndex) {
    if (dragIndex === undefined || hoverIndex === undefined) {
      return { questions, orderUpdateMapper: {} };
    }

    const orderUpdateMapper = {};
    const parentQuestions = questions
      .filter(q => !q.Order.includes('.'))
      .sort((a, b) => Number(a.Order) - Number(b.Order));

    const dragQuestion = parentQuestions[dragIndex];
    parentQuestions.splice(dragIndex, 1);
    parentQuestions.splice(hoverIndex, 0, dragQuestion);

    let startIndex = hoverIndex;
    let inc = 1;
    if (dragIndex < hoverIndex) {
      inc = -1;
    }

    while (startIndex !== dragIndex) {
      orderUpdateMapper[parentQuestions[startIndex].Order] = parentQuestions[startIndex + inc].Order;
      startIndex += inc;
    }
    orderUpdateMapper[parentQuestions[dragIndex].Order] = parentQuestions[hoverIndex].Order;

    return { questions, orderUpdateMapper };
  },

  updateOrder(questions, orderUpdateMapper) {
    const updatedQuestions = _.cloneDeep(questions);
    updatedQuestions.forEach(question => {
      if (question.Order.includes('.')) {
        const parts = question.Order.split('.');
        parts[0] = orderUpdateMapper[parts[0]] || parts[0];
        // eslint-disable-next-line no-param-reassign
        question.Order = parts.join('.');
      } else {
        // eslint-disable-next-line no-param-reassign
        question.Order = orderUpdateMapper[question.Order] || question.Order;
      }
    });

    const orderMap = _.reduce(
      updatedQuestions,
      (acc, question) => {
        acc[question.Order] = false;
        return acc;
      },
      {}
    );

    return { updatedQuestions, showFollowupQuestions: orderMap };
  },

  updateOrderForRemove(state, removedOrders = [], orderUpdateMapper = {}) {
    const { showFollowupQuestions = {} } = state;
    const updatedShowFollowupQuestions = {};

    Object.keys(showFollowupQuestions).forEach(order => {
      if (!removedOrders.includes(order)) {
        const newOrder = orderUpdateMapper[order] || order;
        updatedShowFollowupQuestions[newOrder] = showFollowupQuestions[order];
      }
    });

    return { updatedShowFollowupQuestions };
  },

  collapseFollowupQuestions(showFollowupQuestions, questionOrder, status) {
    return {
      ...showFollowupQuestions,
      [questionOrder]: status,
    };
  },

  collapseAllFollowupQuestions(showFollowupQuestions, status) {
    return _.mapValues(showFollowupQuestions, () => status);
  },

  validateForm(questions) {
    const categoryErrors = {};
    const questionErrors = {};
    let hasErrors = false;

    questions.forEach(question => {
      const order = question.Order;
      if (question && !question.CategoryName?.trim()) {
        categoryErrors[order] = true;
      }
      if (question && !question.Question?.trim()) {
        questionErrors[order] = true;
      }
      if (question?.Question?.trim() && validateUrl(question?.Question?.trim())) {
        questionErrors[order] = true;
      }
      if (!hasErrors && (categoryErrors[order] || questionErrors[order])) hasErrors = true;
    });

    return { hasErrors, categoryErrors, questionErrors };
  },

  renderQuestions(questions, props = {}) {
    const {
      categoryErrors = {},
      questionErrors = {},
      showFollowupQuestions = {},
      removeQuestion,
      onCategoryChange,
      addQuestion,
      onQuestionFieldChange,
      onDragQuestion,
      collapseFollowupQuestions,
      readOnly = false,
      QuestionItemComponent,
      extraProps = {},
      updateDragQuestionId,
      dragQuestionId,
      updateCollapseOfQuestions,
    } = props;

    return questions
      .filter(q => !q.Order.includes('.'))
      .sort((a, b) => Number(a.Order) - Number(b.Order))
      .map((question, index) => {
        return (
          <QuestionItemComponent
            key={question.Order}
            question={question}
            index={index}
            categoryErrors={categoryErrors}
            removeQuestion={removeQuestion}
            questionErrors={questionErrors}
            readOnly={readOnly}
            dragType="question_drag"
            onCategoryChange={onCategoryChange}
            questions={questions}
            addQuestion={addQuestion}
            onQuestionFieldChange={onQuestionFieldChange}
            onDragQuestion={onDragQuestion}
            showFollowupQuestions={showFollowupQuestions}
            collapseFollowupQuestions={collapseFollowupQuestions}
            updateDragQuestionId={updateDragQuestionId}
            dragQuestionId={dragQuestionId}
            updateCollapseOfQuestions={updateCollapseOfQuestions}
            {...extraProps}
          />
        );
      });
  },

  getCurrentLastQuestionOrder(questions) {
    const parentQuestions = questions.filter(q => !q.Order.includes('.'));
    if (parentQuestions.length === 0) return '0';

    const maxOrder = Math.max(...parentQuestions.map(q => Number(q.Order)));
    return String(maxOrder);
  },
};
